/**
 * Tile wordmark logo — used in editor top bar, dashboard header, auth
 * pages, and the loading screen.
 *
 * Production version: lives in src/client/components/shared so the
 * production app doesn't depend on `docs/tile-ui/` (reference material
 * only). Path data is inlined verbatim from the source SVGs so no
 * bundler/asset loader is required.
 *
 * Renders both `.logo-light` and `.logo-dark` variants in the same DOM;
 * a CSS rule on an ancestor `.dark` (or `[data-theme="dark"]`) toggles
 * which is visible. This way nothing in JS has to know which theme is
 * active — the cascade handles it.
 *
 * Default styling (display rules for `.logo-light` / `.logo-dark` and the
 * `.dark .logo .logo-*` overrides) is loaded by whichever caller imports
 * a Logo-aware stylesheet (e.g. `dashboard.css`, `auth.css`,
 * `shared/loadingScreen.css`). Callers using inline styles can show the
 * appropriate variant explicitly via the `theme` prop.
 */
import React, { CSSProperties, ReactNode } from 'react';
interface LogoProps {
  /** Render width in pixels (height auto-scales). Default 80. */
  width?: number;
  /** Optional onClick — used as the dashboard logo where it returns home. */
  onClick?: () => void;
  /** Extra inline style on the container. */
  style?: CSSProperties;
  /** Render only the icon (the two rotated tiles) without the wordmark. */
  iconOnly?: boolean;
  /**
   * Force a specific variant. Default `'auto'` renders both and lets CSS
   * choose. Use `'light'` / `'dark'` when callers don't load a stylesheet
   * with the `.logo-light` / `.logo-dark` rules.
   */
  theme?: 'auto' | 'light' | 'dark';
  /** Extra className on the wrapper. */
  className?: string;
}

const ICON_TILES = (
  <>
    <rect
      width="144.163"
      height="144.163"
      rx="31.0637"
      transform="matrix(0.866025 -0.5 0.866025 0.5 0.301758 126.945)"
      fill="#34363E"
    />
    <rect
      width="144.163"
      height="144.163"
      rx="31.0637"
      transform="matrix(0.866025 -0.5 0.866025 0.5 0 72.082)"
      fill="#1060E0"
    />
    <path
      d="M122.282 51.4211L102.762 91.1984C102.297 92.1446 104.164 92.8971 105.542 92.3196L133.453 80.629C133.884 80.4484 134.432 80.3877 134.948 80.4633L171.805 85.8626C173.494 86.1101 174.742 84.9622 173.621 84.1918L125.383 51.0371C124.457 50.4005 122.675 50.6212 122.282 51.4211Z"
      fill="black"
    />
    <path
      d="M110.868 33.5376L88.4583 86.6534C88.0526 87.6149 89.9893 88.3262 91.3258 87.7066L129.019 70.2307C129.411 70.049 129.917 69.9705 130.414 70.0144L178.98 74.3028C180.682 74.4531 181.738 73.2675 180.537 72.5555L113.898 33.0598C112.914 32.4765 111.202 32.7465 110.868 33.5376Z"
      fill="white"
    />
  </>
);

// Wordmark path — the "tile" letterforms. Same path data, two fills.
const WORDMARK_PATH =
  'M314.197 145.72C306.881 145.72 301.405 143.572 297.771 139.277C294.184 134.934 292.39 128.326 292.39 119.453V80.5122L297.913 85.8931H280.85V74.5649H298.196L292.39 80.0874L294.868 54.9531H306.196V80.0874L300.603 74.5649H327.437V85.8931H300.603L306.196 80.5122V119.311C306.196 124.833 306.999 128.68 308.604 130.852C310.208 132.976 313.04 134.038 317.1 134.038C318.563 134.038 320.262 133.872 322.197 133.542C324.18 133.212 325.903 132.81 327.366 132.338L328.923 143.454C326.611 144.115 324.156 144.658 321.56 145.083C319.011 145.507 316.557 145.72 314.197 145.72ZM345.165 145.012V133.896H359.254V85.6807H345.661V74.5649H373.061V133.896H387.079V145.012H345.165ZM365.98 63.4492C363.432 63.4492 361.26 62.5524 359.467 60.7588C357.673 58.9652 356.776 56.7939 356.776 54.2451C356.776 51.7435 357.673 49.5959 359.467 47.8022C361.26 46.0086 363.432 45.1118 365.98 45.1118C368.529 45.1118 370.677 46.0086 372.423 47.8022C374.217 49.5959 375.114 51.7435 375.114 54.2451C375.114 56.7939 374.217 58.9652 372.423 60.7588C370.677 62.5524 368.529 63.4492 365.98 63.4492ZM402.825 145.012V133.896H418.331V59.98H403.321V48.8643H432.137V133.896H447.571V145.012H402.825ZM494.47 145.861C484.558 145.861 476.911 142.935 471.53 137.082C466.197 131.182 463.53 122.049 463.53 109.682C463.53 103.216 464.309 97.6932 465.866 93.1147C467.424 88.5363 469.571 84.8075 472.309 81.9282C475.094 79.0018 478.304 76.8778 481.938 75.5562C485.62 74.1873 489.561 73.5029 493.762 73.5029C499.237 73.5029 503.981 74.6829 507.993 77.043C512.052 79.3558 515.191 82.707 517.409 87.0967C519.628 91.4863 520.737 96.7492 520.737 102.885C520.737 103.971 520.69 105.505 520.595 107.487C520.548 109.47 520.406 111.499 520.17 113.576H472.521L477.407 109.257C477.218 116.007 477.879 121.223 479.389 124.904C480.947 128.539 483.094 131.064 485.832 132.48C488.57 133.896 491.661 134.604 495.107 134.604C498.836 134.604 502.234 134.179 505.302 133.33C508.37 132.433 511.58 131.206 514.931 129.648L519.462 139.631C515.97 141.613 512.005 143.147 507.568 144.233C503.178 145.319 498.812 145.861 494.47 145.861ZM507.639 110.39V104.868C508.063 98.4012 507.049 93.3271 504.594 89.6455C502.187 85.9167 498.505 84.0522 493.549 84.0522C487.885 84.0522 483.637 86.1763 480.805 90.4243C478.02 94.6724 476.888 100.643 477.407 108.337L472.521 104.514H511.887L507.639 110.39Z';

function FullSvg({
  width,
  wordmarkFill,
  className,
}: {
  width: number;
  wordmarkFill: string;
  className?: string;
}) {
  return (
    <svg
      className={className}
      width={width}
      height={(width * 200) / 527}
      viewBox="0 0 527 200"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      {ICON_TILES}
      <path d={WORDMARK_PATH} fill={wordmarkFill} />
    </svg>
  );
}

function IconSvg({ width }: { width: number }) {
  return (
    <svg
      width={width}
      height={width}
      viewBox="0 0 226 200"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      {ICON_TILES}
    </svg>
  );
}

export default function Logo({
  width = 80,
  onClick,
  style,
  iconOnly,
  theme = 'auto',
  className,
}: LogoProps) {
  const cursor = onClick ? 'pointer' : undefined;

  let inner: ReactNode;
  if (iconOnly) {
    inner = <IconSvg width={width} />;
  } else if (theme === 'light') {
    inner = <FullSvg width={width} wordmarkFill="#1a1a1a" />;
  } else if (theme === 'dark') {
    inner = <FullSvg width={width} wordmarkFill="#e8e8e8" />;
  } else {
    // 'auto' — render both and let CSS pick which to display.
    inner = (
      <>
        <FullSvg width={width} wordmarkFill="#1a1a1a" className="logo-light" />
        <FullSvg width={width} wordmarkFill="#e8e8e8" className="logo-dark" />
      </>
    );
  }

  const cls = ['logo', className].filter(Boolean).join(' ');
  return (
    <div className={cls} onClick={onClick} style={{ cursor, ...style }}>
      {inner}
    </div>
  );
}

export { Logo };
